import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, CreditCard, RefreshCw, Mail, MessageCircle, Phone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dashboard-Widget that surfaces customers whose stored card failed to
 * charge. Clicking the widget opens a detail dialog with quick actions:
 *   • Erneut belasten  (manual retry of all open invoices)
 *   • WhatsApp öffnen  (wa.me link)
 *   • E-Mail verfassen (mailto link)
 *   • Anrufen          (tel link)
 */
export default function FailedPaymentsWidget() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ count: 0, total_amount: 0, customers: [] });
  const [open, setOpen] = useState(false);
  const [retryingCustomerId, setRetryingCustomerId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dashboard/failed-payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || { count: 0, total_amount: 0, customers: [] });
    } catch (err) {
      console.error('Failed to load failed-payments widget', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const retryAllForCustomer = async (customer) => {
    if (!window.confirm(
      `${customer.customer_name}: ${customer.invoice_count} offene Rechnung(en) über CHF ${customer.total_open_amount.toFixed(2)} jetzt erneut belasten?`
    )) return;

    setRetryingCustomerId(customer.customer_id);
    const token = localStorage.getItem('token');
    let ok = 0, fail = 0;
    for (const inv of customer.invoices) {
      try {
        const r = await axios.post(
          `${API}/invoices/${inv.id}/retry-charge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (r.data?.success) ok++; else fail++;
      } catch {
        fail++;
      }
    }
    setRetryingCustomerId(null);
    if (ok > 0 && fail === 0) toast.success(`✅ ${ok} Rechnung(en) erfolgreich belastet`);
    else if (ok > 0 && fail > 0) toast.warning(`⚠️ ${ok} erfolgreich, ${fail} weiterhin fehlgeschlagen`);
    else toast.error(`❌ Alle ${fail} Belastungen fehlgeschlagen`);
    load();
  };

  if (loading) return null;
  if (data.count === 0) return null; // don't show anything when all-clear

  return (
    <>
      <Card
        className="border-2 border-red-300 bg-red-50 cursor-pointer hover:bg-red-100 transition-colors"
        onClick={() => setOpen(true)}
        data-testid="failed-payments-widget"
      >
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-red-100 text-red-600 p-3 rounded-full">
                <CreditCard size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">💳 Zahlungsstörungen</h3>
                <div className="flex gap-4 mt-1 text-sm">
                  <span className="text-red-700 font-medium flex items-center gap-1">
                    <AlertTriangle size={14} />
                    <span data-testid="failed-payments-count">{data.count}</span>&nbsp;Kunde{data.count === 1 ? '' : 'n'}
                  </span>
                  <span className="text-red-700 font-medium">
                    💰 CHF <span data-testid="failed-payments-total">{data.total_amount.toFixed(2)}</span> offen
                  </span>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              data-testid="failed-payments-details-btn"
            >
              Details & Aktionen
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="failed-payments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="text-red-600" size={20} />
              Zahlungsstörungen – {data.count} Kunde{data.count === 1 ? '' : 'n'}
            </DialogTitle>
            <DialogDescription>
              Ein Klick auf <strong>Erneut belasten</strong> versucht, alle offenen Rechnungen des Kunden sofort von der hinterlegten Karte abzubuchen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            {data.customers.map((c) => (
              <div
                key={c.customer_id}
                className="border border-red-200 rounded-lg p-4 bg-white"
                data-testid={`failed-payment-row-${c.customer_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">{c.customer_name}</span>
                      {c.auto_charge_paused && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                          ⛔ Auto-Charge pausiert
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {c.invoice_count} Rechnung{c.invoice_count === 1 ? '' : 'en'} &middot;{' '}
                      <strong className="text-red-700">CHF {c.total_open_amount.toFixed(2)}</strong>
                      {c.max_attempts > 0 && <> &middot; Versuch {c.max_attempts}/4</>}
                    </div>
                    {c.last_error && (
                      <div className="text-xs text-gray-500 mt-1 italic truncate" title={c.last_error}>
                        ❌ {c.last_error}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 shrink-0">
                    {c.phone && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`https://wa.me/${c.phone.replace(/[^0-9+]/g, '')}`, '_blank')}
                          data-testid={`failed-payment-wa-${c.customer_id}`}
                          title="WhatsApp öffnen"
                        >
                          <MessageCircle size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`tel:${c.phone}`, '_self')}
                          data-testid={`failed-payment-call-${c.customer_id}`}
                          title="Anrufen"
                        >
                          <Phone size={14} />
                        </Button>
                      </>
                    )}
                    {c.email && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`mailto:${c.email}`, '_self')}
                        data-testid={`failed-payment-email-${c.customer_id}`}
                        title="E-Mail senden"
                      >
                        <Mail size={14} />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => retryAllForCustomer(c)}
                      disabled={retryingCustomerId === c.customer_id}
                      data-testid={`failed-payment-retry-${c.customer_id}`}
                    >
                      {retryingCustomerId === c.customer_id ? (
                        <RefreshCw size={14} className="animate-spin mr-1" />
                      ) : (
                        <RefreshCw size={14} className="mr-1" />
                      )}
                      Erneut belasten
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
